import { loadLogoInfo } from "./trimmedLogo";
import { alpha, shade, tint } from "./color";
import leagueLogo from "../HslLogo.png";

const W = 1080;
const H = 1350;
const FONT = "Inter, 'Helvetica Neue', Arial, sans-serif";
const RESULT_BG = { W: "#16a34a", D: "#9ca3af", L: "#dc2626", F: "#111111" };
const MEDALS = ["#d4a017", "#9ea7b0", "#b8733c"];
const MOVEMENT = { up: "#4ade80", down: "#f87171" };
export const SITE = "stats.hadafsports.com";

const currentPath = () => (typeof window !== "undefined" ? window.location.pathname : "/");

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const crestImage = async (url) => {
  const info = await loadLogoInfo(url);
  return { img: await loadImage(info?.src), box: info?.box || null, light: !!info?.light };
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const setFont = (ctx, size, weight) => {
  ctx.font = `${weight} ${size}px ${FONT}`;
};

/** Greedy word wrap; a single word wider than the line is broken by characters. */
export const wrapWords = (measure, text, maxWidth) => {
  const lines = [];
  let line = "";
  String(text || "")
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (measure(next) <= maxWidth) {
        line = next;
        return;
      }
      if (line) lines.push(line);
      line = "";
      let chunk = "";
      [...word].forEach((ch) => {
        if (measure(chunk + ch) > maxWidth && chunk) {
          lines.push(chunk);
          chunk = ch;
        } else {
          chunk += ch;
        }
      });
      line = chunk;
    });
  if (line) lines.push(line);
  return lines;
};

/**
 * Lays text out at `size`, spilling onto extra lines (up to `maxLines`) before shrinking.
 * Only if it still doesn't fit does the size step down, and as a last resort the final line is ellipsised.
 */
export const layoutText = (ctx, text, maxWidth, { size, weight = 800, maxLines = 2, minSize = Math.round(size * 0.6), lineHeight = 1.12 }) => {
  for (let s = size; s >= minSize; s -= 2) {
    setFont(ctx, s, weight);
    const lines = wrapWords((t) => ctx.measureText(t).width, text, maxWidth);
    if (lines.length <= maxLines) return { lines, size: s, weight, lh: s * lineHeight };
  }
  setFont(ctx, minSize, weight);
  const lines = wrapWords((t) => ctx.measureText(t).width, text, maxWidth).slice(0, maxLines);
  let last = lines[lines.length - 1] || "";
  while (last && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
  lines[lines.length - 1] = `${last}…`;
  return { lines, size: minSize, weight, lh: minSize * lineHeight };
};

const blockHeight = (block) => block.lines.length * block.lh;

/** Draws a laid-out block with its top at `top`; returns the bottom edge. */
const drawBlock = (ctx, block, x, top, { align = "left", color = "#fff" } = {}) => {
  setFont(ctx, block.size, block.weight);
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  block.lines.forEach((line, i) => ctx.fillText(line, x, top + block.lh * (i + 0.5)));
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  return top + blockHeight(block);
};

const drawCrest = (ctx, crest, x, y, size, name) => {
  ctx.save();
  roundRect(ctx, x, y, size, size, size * 0.22);
  ctx.fillStyle = crest?.light ? "#18181b" : "#ffffff";
  ctx.fill();
  ctx.restore();
  const pad = size * 0.12;
  if (crest?.img) {
    const { img, box } = crest;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const sx = box ? box.x * iw : 0;
    const sy = box ? box.y * ih : 0;
    const sw = box ? box.w * iw : iw;
    const sh = box ? box.h * ih : ih;
    const inner = size - pad * 2;
    const k = inner / Math.max(sw, sh);
    ctx.drawImage(img, sx, sy, sw, sh, x + pad + (inner - sw * k) / 2, y + pad + (inner - sh * k) / 2, sw * k, sh * k);
  } else {
    ctx.fillStyle = "#71717a";
    setFont(ctx, size * 0.34, 800);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(name || "?").slice(0, 2).toUpperCase(), x + size / 2, y + size / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }
};

const MANIFEST_URL = "/share-backgrounds/manifest.json";
let manifestJob = null;

/** League folder name in the manifest for a division label ("DIV 1" → "DIV1"). */
export const backgroundFolder = (division) => String(division || "").toUpperCase().replace(/\s+/g, "");

/** Manifest keys to try, most specific first: a city with its own photos ("MISSISSAUGA") uses them for every division. */
export const backgroundKeys = (division, city) => [city, division].filter(Boolean).map(backgroundFolder);

/** A random photo for the league from `public/share-backgrounds`, or null when it has none. */
export const pickBackground = async (division, city, random = Math.random) => {
  manifestJob = manifestJob || fetch(MANIFEST_URL).then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
  const manifest = await manifestJob;
  const key = backgroundKeys(division, city).find((k) => manifest[k]?.length);
  const files = key ? manifest[key] : [];
  if (!files.length) return null;
  return loadImage(`/share-backgrounds/${files[Math.floor(random() * files.length)]}`);
};

const drawCover = (ctx, img) => {
  const k = Math.max(W / img.naturalWidth, H / img.naturalHeight);
  const w = img.naturalWidth * k;
  const h = img.naturalHeight * k;
  ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
};

const background = (ctx, accent, photo) => {
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, shade(accent, 0.2));
  g.addColorStop(1, shade(accent, 0.72));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  if (photo) {
    drawCover(ctx, photo);
    const veil = ctx.createLinearGradient(0, 0, 0, H);
    veil.addColorStop(0, alpha(shade(accent, 0.55), 0.78));
    veil.addColorStop(0.45, alpha(shade(accent, 0.7), 0.86));
    veil.addColorStop(1, alpha(shade(accent, 0.85), 0.94));
    ctx.fillStyle = veil;
    ctx.fillRect(0, 0, W, H);
  }
  const glow = ctx.createRadialGradient(W - 120, 120, 0, W - 120, 120, 620);
  glow.addColorStop(0, "rgba(255,255,255,0.2)");
  glow.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
};

const FOOTER_Y = H - 70;

let leagueLogoJob = null;

const footer = async (ctx, path = currentPath()) => {
  leagueLogoJob = leagueLogoJob || loadImage(leagueLogo);
  const logo = await leagueLogoJob;
  const url = `${SITE}${path === "/" ? "" : path}`;
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  setFont(ctx, 30, 700);
  const urlLeft = W - 80 - ctx.measureText(url).width;
  let x = 80;
  if (logo) {
    const h = 72;
    const w = (logo.naturalWidth / logo.naturalHeight) * h;
    ctx.drawImage(logo, x, FOOTER_Y - 10 - h / 2, w, h);
    x += w + 18;
  }
  const name = "HADAF SOCCER LEAGUE";
  let size = 30;
  setFont(ctx, size, 700);
  while (size > 18 && x + ctx.measureText(name).width > urlLeft - 28) setFont(ctx, --size, 700);
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText(name, x, FOOTER_Y);
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  setFont(ctx, 30, 700);
  ctx.textAlign = "right";
  ctx.fillText(url, W - 80, FOOTER_Y);
  ctx.textAlign = "left";
};

/** Eyebrow + title at the top of a card; returns the y below them. */
const heading = (ctx, eyebrow, title, top = 80) => {
  let y = top;
  if (eyebrow) {
    const e = layoutText(ctx, eyebrow.toUpperCase(), W - 160, { size: 32, weight: 800, lineHeight: 1.3 });
    y = drawBlock(ctx, e, 80, y, { color: "rgba(255,255,255,0.7)" });
  }
  if (title) {
    const t = layoutText(ctx, title, W - 160, { size: 92, weight: 900, lineHeight: 1.05 });
    y = drawBlock(ctx, t, 80, y + 8);
  }
  return y;
};

const newCanvas = async () => {
  if (document.fonts?.ready) await document.fonts.ready;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  return canvas;
};

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const shortDate = (key) => {
  const [y, m, d] = String(key).split("-").map(Number);
  if (!y || !m || !d) return String(key);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
};

export async function drawTeamCard({ team, league, summary, accent = "#27272a", path }) {
  const canvas = await newCanvas();
  const ctx = canvas.getContext("2d");
  background(ctx, accent);
  let y = heading(ctx, [league?.city, league?.division].filter(Boolean).join(" · "), null, 90);

  const name = layoutText(ctx, team.name, W - 160, { size: 104, weight: 900, lineHeight: 1.04, minSize: 72 });
  const crestSize = name.lines.length > 1 ? 220 : 270;
  const crest = await crestImage(team.logo_url);
  drawCrest(ctx, crest, 80, y + 24, crestSize, team.name);
  y = drawBlock(ctx, name, 80, y + 24 + crestSize + 30);

  const row = summary?.row;
  if (summary?.position && row?.gp > 0) {
    const pos = layoutText(ctx, `${ordinal(summary.position)} of ${summary.teamCount} · ${row.points} pts`, W - 160, { size: 44, weight: 700, lineHeight: 1.25 });
    y = drawBlock(ctx, pos, 80, y + 8, { color: "rgba(255,255,255,0.85)" });
  }

  const form = summary?.form || [];
  if (form.length) {
    y += 20;
    form.forEach((r, i) => {
      const x = 80 + i * 88;
      roundRect(ctx, x, y, 72, 72, 16);
      ctx.fillStyle = RESULT_BG[r] || "#555";
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.4)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "#fff";
      setFont(ctx, 38, 900);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(r, x + 36, y + 38);
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
    });
    y += 72;
  }

  if (row) {
    y += 36;
    const stats = [
      ["Played", row.gp],
      ["Record", `${row.wins}-${row.draws}-${row.losses}`],
      ["Goals", `${row.goalsFor}:${row.goalsAgainst}`],
    ];
    const cw = (W - 160 - 40) / 3;
    stats.forEach(([label, value], i) => {
      const x = 80 + i * (cw + 20);
      roundRect(ctx, x, y, cw, 150, 28);
      ctx.fillStyle = "rgba(255,255,255,0.1)";
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      setFont(ctx, 26, 800);
      ctx.fillText(label.toUpperCase(), x + 32, y + 48);
      ctx.fillStyle = "#fff";
      setFont(ctx, 60, 900);
      ctx.fillText(String(value), x + 32, y + 118);
    });
    y += 150;
  }

  const next = summary?.next;
  if (next) {
    const line = `${next.home ? "vs" : "@"} ${next.opponent?.name || "TBD"}${next.fixture?.date ? ` · ${shortDate(next.fixture.date)}` : ""}`;
    const text = layoutText(ctx, line, W - 232, { size: 44, weight: 800, lineHeight: 1.2 });
    const boxH = 72 + blockHeight(text);
    const top = Math.min(y + 36, FOOTER_Y - 50 - boxH);
    roundRect(ctx, 80, top, W - 160, boxH, 28);
    ctx.fillStyle = tint(shade(accent, 0.85), 0.04);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    setFont(ctx, 24, 800);
    ctx.fillText("NEXT GAME", 116, top + 46);
    drawBlock(ctx, text, 116, top + 58);
  }

  await footer(ctx, path);
  return canvas;
}

/** "18:00" or "18:00:00" → "6:00 PM"; anything else is returned as is. */
export const clockLabel = (t) => {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(t ?? "").trim());
  if (!m) return t;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
};

/** The field on one line, or split at " - " ("Churchill Meadows" / "Field A") when it is too wide. */
const fieldLines = (ctx, field, maxWidth) => {
  if (!field) return [];
  setFont(ctx, 22, 700);
  if (ctx.measureText(field).width <= maxWidth) return [field];
  return String(field).split(/\s+[-–·]\s+/).filter(Boolean);
};

/**
 * Home win / draw / away win segments of a probability bar `width` wide: the favourite's
 * share green, the underdog's red and the draw grey, like the Insights odds bar.
 */
export const oddsSegments = ({ home, draw, away }, width) => {
  const total = home + draw + away || 1;
  const [hc, ac] = home === away ? [RESULT_BG.D, RESULT_BG.D] : home > away ? [RESULT_BG.W, RESULT_BG.L] : [RESULT_BG.L, RESULT_BG.W];
  let x = 0;
  return [
    [home, hc],
    [draw, alpha(RESULT_BG.D, 0.75)],
    [away, ac],
  ].map(([p, color]) => {
    const w = (p / total) * width;
    const seg = { x, w, color, label: `${Math.round((p / total) * 100)}%` };
    x += w;
    return seg;
  });
};

const ODDS_H = 30;

const drawOddsBar = (ctx, odds, x, y, width) => {
  ctx.save();
  roundRect(ctx, x, y, width, ODDS_H, ODDS_H / 2);
  ctx.clip();
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.fillRect(x, y, width, ODDS_H);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  oddsSegments(odds, width).forEach((s) => {
    ctx.fillStyle = s.color;
    ctx.fillRect(x + s.x, y, s.w, ODDS_H);
    const fits = [18, 15].some((size) => {
      setFont(ctx, size, 900);
      return ctx.measureText(s.label).width + 4 <= s.w;
    });
    if (!fits) return;
    ctx.fillStyle = "#fff";
    ctx.fillText(s.label, x + s.x + s.w / 2, y + ODDS_H / 2 + 1);
  });
  ctx.restore();
};

export async function drawMatchdayCard({ title, subtitle, games, path, division, city }) {
  const canvas = await newCanvas();
  const ctx = canvas.getContext("2d");
  background(ctx, "#1f2937", await pickBackground(division, city));
  const top = heading(ctx, subtitle, title) + 40;

  const list = games.slice(0, 6);
  const bottom = FOOTER_Y - 70 - (games.length > list.length ? 50 : 0);
  const rowH = Math.min(190, (bottom - top) / Math.max(1, list.length));
  const crests = await Promise.all(list.flatMap((g) => [crestImage(g.home?.logo_url), crestImage(g.away?.logo_url)]));
  list.forEach((g, i) => {
    const y = top + i * rowH;
    const h = rowH - 18;
    roundRect(ctx, 80, y, W - 160, h, 26);
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    ctx.fill();
    const mid = y + h / 2;
    const cs = Math.min(84, h - 40);
    drawCrest(ctx, crests[i * 2], 108, mid - cs / 2, cs, g.home?.name);
    drawCrest(ctx, crests[i * 2 + 1], W - 108 - cs, mid - cs / 2, cs, g.away?.name);
    const scoreW = 200;
    const nameW = (W - 160 - 56 - cs * 2 - 40 - scoreW) / 2;
    const opts = { size: 34, weight: 800, lineHeight: 1.15, minSize: 24 };
    const home = layoutText(ctx, g.home?.name || "TBD", nameW, opts);
    drawBlock(ctx, home, 128 + cs, mid - blockHeight(home) / 2);
    const away = layoutText(ctx, g.away?.name || "TBD", nameW, opts);
    drawBlock(ctx, away, W - 128 - cs, mid - blockHeight(away) / 2, { align: "right" });
    const small = { size: 22, weight: 700, maxLines: 1, minSize: 16, lineHeight: 1.3 };
    const text = (block, color) => ({ h: blockHeight(block), draw: (cy) => drawBlock(ctx, block, W / 2, cy, { align: "center", color }) });
    const muted = "rgba(255,255,255,0.68)";
    const center = [
      text(layoutText(ctx, g.score || clockLabel(g.time) || "vs", scoreW, { size: g.score ? 56 : 34, weight: 900, maxLines: 1, lineHeight: 1.1 }), "#fff"),
      !g.score && g.odds && { h: ODDS_H + 16, draw: (cy) => drawOddsBar(ctx, g.odds, W / 2 - scoreW / 2, cy + 8, scoreW) },
      g.score && g.predicted && text(layoutText(ctx, `Predicted ${g.predicted}`, scoreW, small), muted),
      ...fieldLines(ctx, g.fixture?.field, scoreW).map((line) => text(layoutText(ctx, line, scoreW, small), muted)),
    ].filter(Boolean);
    let cy = mid - center.reduce((t, c) => t + c.h, 0) / 2;
    center.forEach((c) => {
      c.draw(cy);
      cy += c.h;
    });
  });
  if (games.length > list.length) {
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    setFont(ctx, 30, 700);
    ctx.fillText(`+${games.length - list.length} more`, 80, top + list.length * rowH + 24);
  }
  await footer(ctx, path);
  return canvas;
}

/** Competition ranking on goals: equal goals share a rank (1, 2, 2, 4). */
export const scorerRanks = (players) =>
  players.map((p, i) => {
    let r = i;
    while (r > 0 && Number(players[r - 1].goals || 0) === Number(p.goals || 0)) r -= 1;
    return r + 1;
  });

export async function drawScorersCard({ title = "Top scorers", subtitle, players, path, division, city }) {
  const canvas = await newCanvas();
  const ctx = canvas.getContext("2d");
  background(ctx, "#8a6508", await pickBackground(division, city));
  const list = players.slice(0, 5);
  const ranks = scorerRanks(list);
  let y = heading(ctx, subtitle, title) + 40;

  const gX = W - 250;
  const aX = W - 135;
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  setFont(ctx, 26, 800);
  ctx.textAlign = "center";
  ctx.fillText("GOALS", gX, y);
  ctx.fillText("ASSISTS", aX, y);
  ctx.textAlign = "left";
  y += 24;

  const crests = await Promise.all(list.map((p) => crestImage(p.team?.logo_url)));
  const cs = 92;
  const textX = 194 + cs + 28;
  const textW = gX - 70 - textX;
  const GAP = 6;
  const SPACING = 16;
  const available = FOOTER_Y - 60 - y - SPACING * list.length;
  const layoutRows = (k) =>
    list.map((p) => {
      const name = layoutText(ctx, p.name || "", textW, { size: Math.round(42 * k), weight: 900, lineHeight: 1.1, minSize: Math.round(30 * k) });
      const meta = [p.jersey_number != null && p.jersey_number !== "" ? `#${p.jersey_number}` : null, p.team?.name].filter(Boolean).join(" · ");
      const sub = meta ? layoutText(ctx, meta, textW, { size: Math.round(26 * k), weight: 600, lineHeight: 1.2, minSize: Math.round(22 * k) }) : null;
      return { name, sub, content: blockHeight(name) + (sub ? GAP + blockHeight(sub) : 0) };
    });
  const rowHeights = (rs) => rs.map((r) => Math.max(120, r.content + 40));
  let k = 1;
  let rows = layoutRows(k);
  while (k > 0.7 && rowHeights(rows).reduce((t, n) => t + n, 0) > available) {
    k -= 0.05;
    rows = layoutRows(k);
  }
  const needed = rowHeights(rows);
  const extra = Math.max(0, Math.min(34, (available - needed.reduce((t, n) => t + n, 0)) / Math.max(1, list.length)));
  let top = y;
  list.forEach((p, i) => {
    const h = needed[i] + extra;
    const mid = top + h / 2;
    roundRect(ctx, 80, top, W - 160, h, 28);
    ctx.fillStyle = i === 0 ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.08)";
    ctx.fill();

    const rank = ranks[i];
    ctx.beginPath();
    ctx.arc(140, mid, 30, 0, Math.PI * 2);
    ctx.fillStyle = MEDALS[rank - 1] || "rgba(255,255,255,0.18)";
    ctx.fill();
    ctx.fillStyle = "#fff";
    setFont(ctx, 30, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(rank), 140, mid + 1);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    drawCrest(ctx, crests[i], 194, mid - cs / 2, cs, p.team?.name);
    const { name, sub, content } = rows[i];
    const after = drawBlock(ctx, name, textX, mid - content / 2);
    if (sub) drawBlock(ctx, sub, textX, after + GAP, { color: "rgba(255,255,255,0.65)" });

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    setFont(ctx, 64, 900);
    ctx.fillText(String(Number(p.goals || 0)), gX, mid + 2);
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    setFont(ctx, 48, 800);
    ctx.fillText(String(Number(p.assists || 0)), aX, mid + 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    top += h + SPACING;
  });

  await footer(ctx, path);
  return canvas;
}

/** ▲/▼ with the number of places moved, centred on (x, mid); nothing when unchanged. */
const drawMovement = (ctx, movement, x, mid) => {
  const n = Number(movement) || 0;
  if (!n) return;
  const up = n > 0;
  const tip = up ? mid - 22 : mid + 22;
  const base = up ? mid - 6 : mid + 6;
  ctx.fillStyle = up ? MOVEMENT.up : MOVEMENT.down;
  ctx.beginPath();
  ctx.moveTo(x, tip);
  ctx.lineTo(x + 12, base);
  ctx.lineTo(x - 12, base);
  ctx.closePath();
  ctx.fill();
  setFont(ctx, 20, 800);
  ctx.fillText(String(Math.abs(n)), x, up ? mid + 10 : mid - 10);
};

export async function drawStandingsCard({ title = "Standings", subtitle, rows, path, division, city }) {
  const canvas = await newCanvas();
  const ctx = canvas.getContext("2d");
  background(ctx, "#1e293b", await pickBackground(division, city));
  let y = heading(ctx, subtitle, title) + 40;

  const list = rows.slice(0, 10);
  const cols = [
    ["gp", "GP"],
    ["wins", "W"],
    ["draws", "D"],
    ["losses", "L"],
    ["goalDifference", "GD"],
    ["points", "PTS"],
  ];
  const colW = 72;
  const colX = (i) => W - 80 - 24 - colW * (cols.length - 1 - i) - colW / 2;
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  setFont(ctx, 24, 800);
  ctx.textAlign = "center";
  cols.forEach(([, label], i) => ctx.fillText(label, colX(i), y));
  ctx.textAlign = "left";
  y += 22;

  const rowH = Math.min(100, (FOOTER_Y - 60 - (rows.length > list.length ? 50 : 0) - y) / Math.max(1, list.length));
  const cs = Math.min(60, rowH - 30);
  const crestX = 80 + 108;
  const nameX = crestX + cs + 20;
  const nameW = colX(0) - colW / 2 - 16 - nameX;
  const crests = await Promise.all(list.map((t) => crestImage(t.logo_url)));
  list.forEach((t, i) => {
    const top = y + i * rowH;
    const h = rowH - 10;
    const mid = top + h / 2;
    roundRect(ctx, 80, top, W - 160, h, 22);
    ctx.fillStyle = i === 0 ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.07)";
    ctx.fill();

    ctx.fillStyle = i === 0 ? "#fff" : "rgba(255,255,255,0.7)";
    setFont(ctx, 30, 900);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), 80 + 36, mid + 1);
    drawMovement(ctx, t.movement, 80 + 82, mid);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    drawCrest(ctx, crests[i], crestX, mid - cs / 2, cs, t.name);
    const name = layoutText(ctx, t.name || "", nameW, { size: 32, weight: 800, lineHeight: 1.1, minSize: 22 });
    drawBlock(ctx, name, nameX, mid - blockHeight(name) / 2);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    cols.forEach(([key], c) => {
      const n = Number(t[key]) || 0;
      const strong = key === "points";
      ctx.fillStyle = strong ? "#fff" : key === "goalDifference" && n !== 0 ? (n > 0 ? "#86efac" : "#fca5a5") : "rgba(255,255,255,0.8)";
      setFont(ctx, strong ? 38 : 30, strong ? 900 : 700);
      ctx.fillText(key === "goalDifference" && n > 0 ? `+${n}` : String(n), colX(c), mid + 2);
    });
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  });
  if (rows.length > list.length) {
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    setFont(ctx, 28, 700);
    ctx.fillText(`+${rows.length - list.length} more`, 80, y + list.length * rowH + 30);
  }

  await footer(ctx, path);
  return canvas;
}

/** Shares the card through the native share sheet when files are supported, otherwise downloads it. */
export async function shareCanvas(canvas, filename, text) {
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) return "failed";
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
    }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  return "downloaded";
}
